import React, { useState, useEffect } from 'react';
import { MessageSquare, Trash2, Mail, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import { useSite } from '@/context/SiteContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMessages = () => {
  const { isAdmin } = useSite();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Get stored credentials from localStorage
  const getAuthHeader = () => {
    const credentials = localStorage.getItem('admin_credentials');
    if (credentials) {
      return `Basic ${credentials}`;
    }
    return '';
  };

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/messages`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMessages();
    }
  }, [isAdmin]);

  const markAsRead = async (messageId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/messages/${messageId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, read: true } : m
        ));
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const openDelete = (message) => {
    setSelectedMessage(message);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedMessage) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/messages/${selectedMessage.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        setMessages(prev => prev.filter(m => m.id !== selectedMessage.id));
        toast.success('Message deleted');
      }
    } catch (error) {
      toast.error('Failed to delete message');
    }
    setDeleteDialogOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout title="Messages">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="font-serif text-2xl">Contact Messages</h2>
          <p className="text-muted-foreground text-sm">
            Messages from your website contact form
          </p>
        </div>

        {/* Messages list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : messages.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-serif text-xl mb-2">No messages yet</h3>
              <p className="text-muted-foreground">
                Messages from your contact form will appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <Card 
                key={message.id} 
                className={`bg-card border-border ${!message.read ? 'border-l-4 border-l-foreground' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium">{message.name}</h3>
                        {!message.read && (
                          <span className="px-2 py-0.5 text-xs bg-foreground text-background rounded">
                            New
                          </span>
                        )}
                      </div>
                      
                      <a 
                        href={`mailto:${message.email}`}
                        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                      >
                        <Mail className="h-3 w-3" />
                        {message.email}
                      </a>
                      
                      {message.subject && (
                        <p className="text-sm font-medium mt-3">
                          Subject: {message.subject}
                        </p>
                      )}
                      
                      <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">
                        {message.message}
                      </p>
                      
                      <p className="text-xs text-muted-foreground mt-4">
                        {formatDate(message.created_at)}
                      </p>
                    </div>
                    
                    <div className="flex gap-2 flex-shrink-0">
                      {!message.read && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => markAsRead(message.id)}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Mark Read
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openDelete(message)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Message</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this message from {selectedMessage?.name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
